/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React from 'react'
import { App } from 'antd'
import { TextInput } from '../../ts-components/TextInput'
import ImageUploader from '../../ts-components/ImageUploader'
import RichTextEditor from 'src/pages/Schedule/RegistrationSetup/forms/CustomForm/RichTextEditor'
import { FormAction } from '../reducer/useFeedPostReducer'
import colors from '@sportninja/common/constants/appColors'

interface PostFormProps {
  title: string
  content: string
  url: string
  image: string
  isLoading: boolean
  showImage: boolean
  showLink: boolean
  fieldErrors: string[]
  customError: { [key: string]: string }
  dispatch: React.Dispatch<FormAction>
}

/**
 * PostForm component - handles the main form inputs for post creation
 *
 * This component contains all the primary form fields for creating/editing a post:
 * - Title input (required)
 * - Rich text editor for content (required)
 * - Image uploader (conditional based on showImage)
 * - Link URL input (conditional based on showLink)
 *
 * @param props - Component props for form state values and dispatch function
 * @returns JSX element representing the main post form section
 */
export const PostForm: React.FC<PostFormProps> = ({
  title,
  content,
  url,
  image,
  isLoading,
  showImage,
  showLink,
  fieldErrors,
  customError,
  dispatch,
}) => {
  console.log('🔄 PostForm: Rendering component')

  const { message } = App.useApp()

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 16px;
        flex: 1;
      `}
    >
      {/* Horizontal Divider above title */}
      <div
        css={css`
          width: 100%;
          height: 1px;
          background: ${colors.SECONDARY_100};
        `}
      />

      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 8px;
        `}
      >
        <TextInput
          name='title'
          value={title}
          disabled={isLoading}
          setValue={(value) =>
            dispatch({
              type: 'SET_FIELD',
              field: 'title',
              payload: value,
            })
          }
          fieldErrors={fieldErrors}
          customError={customError}
          label='Title *'
          placeholder='Add a Title'
        />
      </div>

      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 8px;
        `}
      >
        <RichTextEditor
          value={content}
          onChange={(value) =>
            dispatch({
              type: 'SET_FIELD',
              field: 'content',
              payload: value,
            })
          }
          placeholder='Share Your Update...'
          label='Content *'
          fieldErrors={fieldErrors}
          customError={customError}
          name='content'
        />
      </div>
      {showImage && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 8px;
          `}
        >
          <ImageUploader
            setImageChanged={(value) =>
              dispatch({
                type: 'SET_FIELD',
                field: 'imageChanged',
                payload: value,
              })
            }
            setImageUrl={(value) =>
              dispatch({
                type: 'SET_FIELD',
                field: 'image',
                payload: value,
              })
            }
            imageUrl={image}
            label='Add Image'
            tip=''
            messageApi={message}
          />
        </div>
      )}
      {showLink && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            flex: 1 0 0;
          `}
        >
          <TextInput
            label='Add Link'
            value={url}
            setValue={(value) =>
              dispatch({
                type: 'SET_FIELD',
                field: 'url',
                payload: value,
              })
            }
            fieldErrors={fieldErrors}
            customError={customError}
            disabled={false}
            name='website'
            type='url'
            placeholder='https://www.sportninja.com'
          />
        </div>
      )}
    </div>
  )
}
